import React from 'react';
import BaseBlock from './BaseBlock';
import { VaultRefsLine } from './VaultRefsLine';

/**
 * ProjectBlock renders projects (Project title, Tech stack, links, Bullet details).
 * Supports: list, cards, case-study, timeline, feature-block variants.
 */
export const ProjectBlock = ({ items = [], variant = 'list' }) => {
  const getTechStackString = (tech) => {
    if (!tech) return '';
    const array = Array.isArray(tech) 
      ? tech 
      : typeof tech === 'string' 
        ? tech.split(/,\s*|  *•  */).filter(Boolean) 
        : [];
    return array.join('  •  ');
  };

  if (!items || items.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-item-mb, 12px)' }}>
      {items.map((proj, idx) => {
        const name = proj.name || proj.title || 'Project Name';
        const rawTech = proj.techStack || proj.tech || proj.technologies;
        const techStr = getTechStackString(rawTech);

        const githubLink = proj.github || proj.link || proj.url || '';
        const demoLink = proj.demo || '';

        // Resolve description: handle string vs list representation
        const description = (proj.description && typeof proj.description === 'string') ? proj.description : '';
        const bullets = Array.isArray(proj.bullets) 
          ? proj.bullets 
          : Array.isArray(proj.points) 
            ? proj.points 
            : Array.isArray(proj.description) 
              ? proj.description 
              : [];

        // 1. Boxed Cards Layout
        if (variant === 'cards') {
          return (
            <div 
              key={idx} 
              style={{ 
                border: '1px solid var(--color-border, #E2E8F0)', 
                borderRadius: '6px', 
                padding: '12px', 
                backgroundColor: 'var(--color-bg-page, #FFFFFF)',
                width: '100%',
                pageBreakInside: 'avoid',
                breakInside: 'avoid'
              }}
            >
              <BaseBlock
                title={name}
                subtitle={techStr}
                rightMeta={proj.duration || proj.date || ''}
                bullets={bullets}
              >
                {description && (
                  <p style={{ fontSize: 'var(--font-size-body, 8.5pt)', color: 'var(--color-text-main, #334155)', margin: '4px 0 0', lineHeight: 1.45, textAlign: 'justify' }}>
                    {description}
                  </p>
                )}
              </BaseBlock>
              {(githubLink || demoLink) && (
                <div style={{ fontSize: 'var(--font-size-meta, 8pt)', marginTop: '6px', display: 'flex', gap: '12px' }}>
                  {githubLink && <a href={githubLink} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>💻 Github</a>}
                  {demoLink && <a href={demoLink} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>⚡ Demo</a>}
                </div>
              )}
              <VaultRefsLine refs={proj.vault_refs} />
            </div>
          );
        }

        // 2. Feature Block (Left border highlight & shaded background)
        if (variant === 'feature-block') {
          return (
            <div 
              key={idx} 
              style={{ 
                borderLeft: '4px solid var(--color-primary, #16324F)', 
                backgroundColor: 'var(--color-border, #F8FAFC)', 
                padding: '10px 14px', 
                borderRadius: '0 6px 6px 0',
                width: '100%',
                pageBreakInside: 'avoid',
                breakInside: 'avoid'
              }}
            >
              <BaseBlock
                title={name}
                subtitle={techStr}
                rightMeta={proj.duration || proj.date || ''}
                bullets={bullets}
              >
                {description && (
                  <p style={{ fontSize: 'var(--font-size-body, 8.5pt)', color: 'var(--color-text-main, #334155)', margin: '4px 0 0', lineHeight: 1.45, textAlign: 'justify' }}>
                    {description}
                  </p>
                )}
              </BaseBlock>
              <VaultRefsLine refs={proj.vault_refs} />
            </div>
          );
        }

        if (variant === 'timeline') {
          return (
            <div key={idx} style={{ position: 'relative', borderLeft: '2px solid var(--color-border, #E2E8F0)', paddingLeft: '16px', marginLeft: '6px', width: '100%' }}>
              <div 
                style={{ 
                  position: 'absolute', 
                  left: '-23px', 
                  top: '4px', 
                  width: '12px', 
                  height: '12px', 
                  borderRadius: '50%', 
                  backgroundColor: 'var(--color-primary, #16324F)',
                  border: '2px solid var(--color-bg-page, #FFFFFF)'
                }} 
              />
              <BaseBlock
                title={name}
                subtitle={techStr}
                rightMeta={proj.duration || proj.date || ''}
                bullets={bullets}
              >
                {description && (
                  <p style={{ fontSize: 'var(--font-size-body, 8.5pt)', color: 'var(--color-text-main, #334155)', margin: '4px 0 0', lineHeight: 1.45, textAlign: 'justify' }}>
                    {description}
                  </p>
                )}
              </BaseBlock>
              <VaultRefsLine refs={proj.vault_refs} />
            </div>
          );
        }

        // 4. Case Study Layout (Italic summary text description first, bullets second)
        if (variant === 'case-study') {
          return (
            <div key={idx} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
                <strong style={{ fontSize: 'var(--font-size-subheading, 9.5pt)', color: 'var(--color-text-main, #2C3E50)' }}>{name}</strong>
                <span style={{ fontSize: 'var(--font-size-meta, 8pt)', color: 'var(--color-text-light, #7F8C8D)', fontStyle: 'italic' }}>{proj.duration || proj.date || ''}</span>
              </div>
              <div style={{ fontSize: 'var(--font-size-body, 8.5pt)', color: 'var(--color-secondary, #204F73)', fontStyle: 'italic', marginBottom: '4px' }}>
                {techStr}
              </div>
              {description && (
                <p style={{ fontSize: 'var(--font-size-body, 8.5pt)', color: 'var(--color-text-main, #334155)', margin: '0 0 6px 0', lineHeight: 1.45, textAlign: 'justify' }}>
                  {description}
                </p>
              )}
              {bullets.length > 0 && (
                <ul style={{ margin: '0', paddingLeft: '20px', listStyleType: 'circle' }}>
                  {bullets.map((bullet, i) => (
                    <li key={i} style={{ fontSize: 'var(--font-size-bullet, 8.8pt)', lineHeight: 'var(--line-height-bullet, 1.35)', color: 'var(--color-text-main)', marginBottom: 'var(--spacing-bullet-gap, 4px)' }}>
                      {bullet}
                    </li>
                  ))}
                </ul>
              )}
              <VaultRefsLine refs={proj.vault_refs} />
            </div>
          );
        }

        // 5. Standard / List Layout
        return (
          <div key={idx}>
            <BaseBlock
              title={name}
              subtitle={techStr}
              rightMeta={proj.duration || proj.date || ''}
              bullets={bullets}
            >
              {description && (
                <p style={{ fontSize: 'var(--font-size-body, 8.5pt)', color: 'var(--color-text-main, #334155)', margin: '4px 0 0', lineHeight: 1.45, textAlign: 'justify' }}>
                  {description}
                </p>
              )}
              {(githubLink || demoLink) && (
                <div style={{ fontSize: 'var(--font-size-meta, 8pt)', marginTop: '4px', display: 'flex', gap: '12px' }}>
                  {githubLink && <a href={githubLink} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>💻 Github</a>}
                  {demoLink && <a href={demoLink} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>⚡ Demo</a>}
                </div>
              )}
            </BaseBlock>
            <VaultRefsLine refs={proj.vault_refs} />
          </div>
        );
      })}
    </div>
  );
};
export default ProjectBlock;
